import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';
import Navigation from './Navigation';
import Footer from './Footer';
import ProtectedText, { PROTECTED_TEXTS } from './ProtectedText';
import LowStockAlert from './LowStockAlert';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Package2, 
  Plus, 
  Clock, 
  CheckCircle,
  Trash2,
  DollarSign,
  ShoppingBag,
  Calculator,
  Truck,
  Camera,
  Image,
  X,
  FileImage,
  Download,
  Eye,
  Edit,
  Save
} from 'lucide-react';

const StockEntry = () => {
  const { user, API } = useContext(UserContext);
  
  // Form states
  const [selectedCategory, setSelectedCategory] = useState('');
  const [customDesignation, setCustomDesignation] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [totalPrice, setTotalPrice] = useState('');
  const [supplier, setSupplier] = useState('');
  const [receipts, setReceipts] = useState([]);
  const [notes, setNotes] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [recentEntries, setRecentEntries] = useState([]);
  const [isAutoCalculate, setIsAutoCalculate] = useState(true);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editData, setEditData] = useState({});
  const [detailedStock, setDetailedStock] = useState([]);
  const [showBatchDetails, setShowBatchDetails] = useState({});

  // Catégories de produits fréquents avec prix suggérés
  const productCategories = [
    // Farines
    { id: 'farine_malika_52kg', name: 'Farine Malika 52KG', category: 'Farines', suggestedPrice: 12500 },
    { id: 'farine_malika_26kg', name: 'Farine Malika 26KG', category: 'Farines', suggestedPrice: 6500 },
    { id: 'farine_malika_beignet_25kg', name: 'Farine Malika beignet 25KG', category: 'Farines', suggestedPrice: 6000 },
    { id: 'farine_doucine_patissiere_25kg', name: 'Farine doucine (pâtissière) 25KG', category: 'Farines', suggestedPrice: 7000 },
    { id: 'farine_doucine_500g', name: 'Farine doucine 500g', category: 'Farines', suggestedPrice: 400 },
    
    // Sucre & Édulcorants
    { id: 'sucre_cristallise_50kg', name: 'Sucre cristallisé 50kg', category: 'Sucre', suggestedPrice: 25000 },
    { id: 'sucre_roux_25kg', name: 'Sucre roux 25kg', category: 'Sucre', suggestedPrice: 15000 },
    { id: 'sucre_glace_1kg', name: 'Sucre glace 1kg', category: 'Sucre', suggestedPrice: 800 },
    
    // Œufs & Laitages
    { id: 'oeufs_frais_30_pieces', name: 'Œufs frais (30 pièces)', category: 'Œufs & Laitages', suggestedPrice: 3000 },
    { id: 'lait_en_poudre_1kg', name: 'Lait en poudre 1kg', category: 'Œufs & Laitages', suggestedPrice: 2500 },
    { id: 'beurre_1kg', name: 'Beurre 1kg', category: 'Œufs & Laitages', suggestedPrice: 3500 },
    
    // Levures & Additifs
    { id: 'levure_fraiche_500g', name: 'Levure fraîche 500g', category: 'Levures', suggestedPrice: 1500 },
    { id: 'levure_seche_100g', name: 'Levure sèche 100g', category: 'Levures', suggestedPrice: 800 },
    { id: 'sel_fin_1kg', name: 'Sel fin 1kg', category: 'Levures', suggestedPrice: 300 },
    { id: 'vanille_liquide_250ml', name: 'Vanille liquide 250ml', category: 'Levures', suggestedPrice: 1200 },
    
    // Autre
    { id: 'autre', name: 'Autre produit (saisie libre)', category: 'Divers', suggestedPrice: 0 }
  ];

  // Load data with date filter
  useEffect(() => {
    let mounted = true;
    
    const loadData = async () => {
      try {
        setPageLoading(true);
        
        const [entriesRes, detailedRes] = await Promise.all([
          axios.get(`${API}/stock?date=${selectedDate}`).catch(() => ({ data: [] })),
          axios.get(`${API}/stock/availability-detailed`).catch(() => ({ data: [] }))
        ]);
        
        if (mounted) {
          setRecentEntries(Array.isArray(entriesRes.data) ? entriesRes.data.slice(0, 20) : []);
          setDetailedStock(Array.isArray(detailedRes.data) ? detailedRes.data : []);
        }
      } catch (error) {
        console.error('Error loading stock data:', error);
        if (mounted) {
          toast.error('Erreur lors du chargement');
          setRecentEntries([]);
          setDetailedStock([]);
        }
      } finally {
        if (mounted) {
          setPageLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      mounted = false;
    };
  }, [API, selectedDate]);

  // Auto-calculate total price
  useEffect(() => {
    if (isAutoCalculate && quantity && unitPrice) {
      const calculated = parseFloat(quantity) * parseFloat(unitPrice);
      setTotalPrice(calculated.toString());
    }
  }, [quantity, unitPrice, isAutoCalculate]);

  // Handle product category selection
  const handleCategorySelect = (categoryId) => {
    try {
      const selectedProduct = productCategories.find(p => p.id === categoryId);
      
      if (selectedProduct) {
        setSelectedCategory(categoryId);
        
        if (categoryId === 'autre') {
          // Reset for custom input
          setCustomDesignation('');
          setUnitPrice('');
        } else {
          // Set suggested price
          setUnitPrice(selectedProduct.suggestedPrice.toString());
          setCustomDesignation(''); // Clear custom input
        }
        
        console.log('Selected product category:', selectedProduct.name);
      }
    } catch (error) {
      console.error('Error selecting product category:', error);
      toast.error('Erreur lors de la sélection du produit');
    }
  };

  // Get final designation
  const getFinalDesignation = () => {
    if (selectedCategory === 'autre') {
      return customDesignation.trim();
    }
    const selectedProduct = productCategories.find(p => p.id === selectedCategory);
    return selectedProduct ? selectedProduct.name : '';
  };

  // Handle receipt photo upload
  const handleReceiptUpload = (event) => {
    try {
      const files = Array.from(event.target.files || []);
      
      files.forEach(file => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const base64 = e.target.result;
              setReceipts(prev => [...prev, {
                id: Date.now() + Math.random(),
                data: base64,
                name: file.name,
                size: file.size
              }]);
            } catch (error) {
              console.error('Error processing receipt:', error);
            }
          };
          reader.readAsDataURL(file);
        } else {
          toast.error(`Type de fichier non supporté: ${file.name}`);
        }
      });
      
      event.target.value = '';
    } catch (error) {
      console.error('Error uploading receipt:', error);
      toast.error('Erreur lors du téléchargement du reçu');
    }
  };

  const removeReceipt = (receiptId) => {
    try {
      setReceipts(prev => prev.filter(receipt => receipt.id !== receiptId));
    } catch (error) {
      console.error('Error removing receipt:', error);
    }
  };

  const downloadReceipt = (receipt) => {
    try {
      const link = document.createElement('a');
      link.href = receipt.data;
      link.download = receipt.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Safe form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const finalDesignation = getFinalDesignation();
      
      if (!finalDesignation || !quantity || !unitPrice || !totalPrice) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }

      if (parseFloat(quantity) <= 0 || parseFloat(unitPrice) <= 0 || parseFloat(totalPrice) <= 0) {
        toast.error('Les valeurs doivent être positives');
        return;
      }

      setLoading(true);

      const receiptData = receipts.map(receipt => receipt.data);

      await axios.post(`${API}/stock`, {
        designation: finalDesignation,
        quantity: parseFloat(quantity),
        unit_price: parseFloat(unitPrice),
        total_price: parseFloat(totalPrice),
        supplier: supplier.trim() || null,
        receipts: receiptData,
        notes: notes.trim() || null,
        user_id: user.id
      });

      toast.success('Achat de stock enregistré avec succès !');
      
      // Reset form
      setSelectedCategory('');
      setCustomDesignation('');
      setQuantity('');
      setUnitPrice('');
      setTotalPrice('');
      setSupplier('');
      setReceipts([]);
      setNotes('');
      
      // Refresh recent entries
      const entriesRes = await axios.get(`${API}/stock?date=${selectedDate}`);
      if (Array.isArray(entriesRes.data)) {
        setRecentEntries(entriesRes.data.slice(0, 20));
      }
    } catch (error) {
      console.error('Error creating stock record:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  // Safe delete
  const handleDelete = async (entryId) => {
    try {
      if (!entryId) return;
      
      if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet enregistrement de stock ?')) {
        return;
      }

      await axios.delete(`${API}/stock/${entryId}?user_role=${user.role}`);
      toast.success('Enregistrement supprimé !');
      
      const entriesRes = await axios.get(`${API}/stock?date=${selectedDate}`);
      if (Array.isArray(entriesRes.data)) {
        setRecentEntries(entriesRes.data.slice(0, 20));
      }
    } catch (error) {
      console.error('Error deleting stock record:', error);
      if (error.response?.status === 403) {
        toast.error('Accès refusé. Seuls les propriétaires et administrateurs peuvent supprimer.');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const canDelete = () => {
    return user && ['manager', 'owner', 'system_admin'].includes(user.role);
  };

  const startEditStock = (entry) => {
    setEditingEntry(entry.id);
    setEditData({
      designation: entry.designation,
      quantity: entry.quantity?.toString() || '',
      unit_price: entry.unit_price?.toString() || '',
      total_price: entry.total_price?.toString() || '',
      supplier: entry.supplier || '',
      notes: entry.notes || ''
    });
  };

  const cancelEditStock = () => {
    setEditingEntry(null);
    setEditData({});
  };

  const saveEditStock = async (entryId) => {
    try {
      if (!editData.designation || !editData.quantity || !editData.unit_price || !editData.total_price) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }

      await axios.put(`${API}/stock/${entryId}?user_role=${user.role}`, {
        designation: editData.designation,
        quantity: parseFloat(editData.quantity),
        unit_price: parseFloat(editData.unit_price),
        total_price: parseFloat(editData.total_price),
        supplier: editData.supplier || null,
        receipts: [], // Garder les reçus existants
        notes: editData.notes || null,
        user_id: user.id
      });

      toast.success('Stock modifié avec succès !');
      
      // Reset et actualiser
      setEditingEntry(null);
      setEditData({});
      
      const entriesRes = await axios.get(`${API}/stock?date=${selectedDate}`);
      if (Array.isArray(entriesRes.data)) {
        setRecentEntries(entriesRes.data.slice(0, 20));
      }
      
    } catch (error) {
      console.error('Error updating stock:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Heure inconnue';
    try {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Heure invalide';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date invalide';
    }
  };

  const formatCurrency = (amount) => {
    return `${amount.toLocaleString()} F CFA`;
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement du stock...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Navigation />
      
      <div className="content-wrapper px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-width-container flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 fade-in">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 no-transform" spellCheck="false">
              <ProtectedText>Gestion du {PROTECTED_TEXTS.STOCK}</ProtectedText>
            </h1>
            <p className="text-gray-600 no-transform" spellCheck="false">
              <ProtectedText>Enregistrez les achats de produits et matières premières</ProtectedText>
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-4 mt-4 sm:mt-0">
            {/* Alerte Stock Faible */}
            <LowStockAlert threshold={5} autoCheck={true} />
            
            <div className="flex items-center space-x-4">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="stock-date-picker"
              />
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                size="sm"
                className="btn-hover"
              >
                Actualiser
              </Button>
            </div>
          </div>
        </div>

        <div className="max-width-cards grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Stock Form */}
          <Card className="bg-white/70 glass border-0 shadow-lg slide-up">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package2 className="h-5 w-5 text-indigo-600" />
                <ProtectedText>Nouvel Achat de {PROTECTED_TEXTS.STOCK}</ProtectedText>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Product Category Selection */}
                <div className="space-y-2">
                  <Label htmlFor="productCategory" className="text-sm font-medium text-gray-700 no-transform" spellCheck="false">
                    <ProtectedText>{PROTECTED_TEXTS.DESIGNATION} du produit *</ProtectedText>
                  </Label>
                  <select
                    id="productCategory"
                    value={selectedCategory}
                    onChange={(e) => handleCategorySelect(e.target.value)}
                    className="w-full h-12 px-4 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                    data-testid="product-category-select"
                    required
                  >
                    <option value="" disabled>Sélectionnez un produit</option>
                    
                    <optgroup label="🌾 Farines">
                      {productCategories.filter(p => p.category === 'Farines').map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </optgroup>
                    
                    <optgroup label="🍯 Sucre & Édulcorants">
                      {productCategories.filter(p => p.category === 'Sucre').map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </optgroup>
                    
                    <optgroup label="🥚 Œufs & Laitages">
                      {productCategories.filter(p => p.category === 'Œufs & Laitages').map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </optgroup>
                    
                    <optgroup label="🧪 Levures & Additifs">
                      {productCategories.filter(p => p.category === 'Levures').map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </optgroup>
                    
                    <optgroup label="📝 Autre">
                      {productCategories.filter(p => p.category === 'Divers').map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                {/* Custom Designation Input - Only if "Autre" selected */}
                {selectedCategory === 'autre' && (
                  <div className="space-y-2">
                    <Label htmlFor="customDesignation" className="text-sm font-medium text-gray-700">
                      <ProtectedText>Précisez le produit</ProtectedText>
                    </Label>
                    <Input
                      id="customDesignation"
                      type="text"
                      value={customDesignation}
                      onChange={(e) => setCustomDesignation(e.target.value)}
                      className="h-12 text-base"
                      placeholder="Ex: Chocolat noir, Noix de coco râpée..."
                      data-testid="custom-designation-input"
                      required
                      spellCheck="false"
                      autoCorrect="off"
                      autoCapitalize="off"
                    />
                  </div>
                )}

                {/* Quantity */}
                <div className="space-y-2">
                  <Label htmlFor="quantity" className="text-sm font-medium text-gray-700 no-transform" spellCheck="false">
                    <ProtectedText>{PROTECTED_TEXTS.QUANTITE} *</ProtectedText>
                  </Label>
                  <Input
                    id="quantity"
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="h-12 text-base"
                    placeholder="Ex: 5 ou 2.5"
                    data-testid="quantity-input"
                    required
                  />
                </div>

                {/* Unit Price */}
                <div className="space-y-2">
                  <Label htmlFor="unitPrice" className="text-sm font-medium text-gray-700">
                    Prix unitaire (F CFA) *
                  </Label>
                  <Input
                    id="unitPrice"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={unitPrice}
                    onChange={(e) => setUnitPrice(e.target.value)}
                    className="h-12 text-base"
                    placeholder="Ex: 12500"
                    data-testid="unit-price-input"
                    required
                  />
                </div>

                {/* Auto-calculate toggle */}
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="autoCalculate"
                    checked={isAutoCalculate}
                    onChange={(e) => setIsAutoCalculate(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <Label htmlFor="autoCalculate" className="text-sm text-blue-800 font-medium">
                    Calculer automatiquement le prix total (Quantité × Prix unitaire)
                  </Label>
                </div>

                {/* Total Price */}
                <div className="space-y-2">
                  <Label htmlFor="totalPrice" className="text-sm font-medium text-gray-700">
                    Prix total (F CFA) *
                  </Label>
                  <Input
                    id="totalPrice"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={totalPrice}
                    onChange={(e) => setTotalPrice(e.target.value)}
                    className="h-12 text-base"
                    placeholder="Calcul automatique ou saisie manuelle"
                    data-testid="total-price-input"
                    disabled={isAutoCalculate}
                    required
                  />
                </div>

                {/* Supplier */}
                <div className="space-y-2">
                  <Label htmlFor="supplier" className="text-sm font-medium text-gray-700">
                    Fournisseur (optionnel)
                  </Label>
                  <Input
                    id="supplier"
                    type="text"
                    value={supplier}
                    onChange={(e) => setSupplier(e.target.value)}
                    className="h-12 text-base"
                    placeholder="Ex: SIFCA, Boutique Ali, Marché central..."
                    data-testid="supplier-input"
                  />
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-sm font-medium text-gray-700">
                    Notes (optionnel)
                  </Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="min-h-[80px] text-base"
                    placeholder="Notes additionnelles, observations..."
                    data-testid="notes-input"
                  />
                </div>

                {/* Receipt Upload */}
                <div className="space-y-2">
                  <Label htmlFor="receipts" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Camera className="h-4 w-4" />
                    Reçus d'achat (optionnel)
                  </Label>
                  <input
                    id="receipts"
                    type="file"
                    accept="image/*"
                    multiple
                    capture="environment"
                    onChange={handleReceiptUpload}
                    className="hidden"
                  />
                  
                  <div className="space-y-3">
                    <Button
                      type="button"
                      onClick={() => document.getElementById('receipts').click()}
                      variant="outline"
                      className="w-full h-12 border-2 border-dashed border-gray-300 hover:border-indigo-400 bg-gray-50 hover:bg-indigo-50"
                      data-testid="receipt-upload-btn"
                    >
                      <Camera className="h-5 w-5 mr-2" />
                      <ProtectedText>Prendre une photo du reçu</ProtectedText>
                    </Button>
                    
                    {/* Receipt Preview */}
                    {receipts.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <FileImage className="h-4 w-4" />
                          {receipts.length} reçu(s) ajouté(s)
                        </p>
                        <div className="grid grid-cols-2 gap-3 max-h-32 overflow-y-auto">
                          {receipts.map((receipt) => (
                            <div key={receipt.id} className="relative group">
                              <img
                                src={receipt.data}
                                alt="Reçu d'achat"
                                className="w-full h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:border-indigo-400 transition-colors"
                                onClick={() => {
                                  // Open full-size view
                                  const modal = window.open('', '_blank');
                                  modal.document.write(`
                                    <html>
                                      <head><title>Reçu - ${receipt.name}</title></head>
                                      <body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;min-height:100vh;">
                                        <img src="${receipt.data}" style="max-width:100%;max-height:100%;object-fit:contain;" alt="Reçu d'achat" />
                                      </body>
                                    </html>
                                  `);
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => removeReceipt(receipt.id)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Supprimer ce reçu"
                              >
                                <X className="h-3 w-3" />
                              </button>
                              <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded">
                                {formatFileSize(receipt.size)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Price Preview */}
                {quantity && unitPrice && (
                  <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="col-span-2 mb-2">
                        <span className="text-indigo-800 font-medium">Produit sélectionné:</span>
                        <span className="font-semibold ml-2 no-transform" spellCheck="false">
                          {getFinalDesignation() || 'Aucun produit sélectionné'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-indigo-800">{PROTECTED_TEXTS.QUANTITE}:</span>
                        <span className="font-semibold">{quantity}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-indigo-800">Prix unitaire:</span>
                        <span className="font-semibold">{formatCurrency(parseFloat(unitPrice || 0))}</span>
                      </div>
                      <div className="col-span-2 flex items-center justify-between border-t border-indigo-200 pt-2">
                        <span className="text-indigo-800 font-medium">Prix total:</span>
                        <span className="text-lg font-bold text-indigo-600">
                          {formatCurrency(parseFloat(totalPrice || 0))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading || !getFinalDesignation() || !quantity || !unitPrice || !totalPrice}
                  className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium rounded-lg shadow-lg"
                  data-testid="submit-stock-btn"
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      <ProtectedText>Enregistrement...</ProtectedText>
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      <ProtectedText>{PROTECTED_TEXTS.ENREGISTRER} l'Achat</ProtectedText>
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Recent Stock Entries */}
          <Card className="bg-white/70 glass border-0 shadow-lg slide-up" style={{animationDelay: '0.1s'}}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-indigo-600" />
                  Achats du {formatDate(selectedDate)}
                </div>
                <Badge variant="secondary" className="bg-indigo-100 text-indigo-800">
                  {recentEntries.length} achat(s)
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Array.isArray(recentEntries) && recentEntries.length > 0 ? (
                <div className="space-y-3">
                  {recentEntries.map((entry, index) => {
                    if (!entry || !entry.id) return null;
                    
                    return (
                      <div key={`stock-entry-${entry.id}-${index}`} className="p-3 bg-gray-50 rounded-lg border-l-4 border-indigo-500">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <div className="p-2 rounded-full bg-indigo-100 text-indigo-600">
                              <Package2 className="h-4 w-4" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">
                                {entry.designation}
                              </p>
                              <p className="text-sm text-gray-600">
                                📅 {formatDate(entry.date)} à {formatTime(entry.time)} • Qté: {entry.quantity} • {formatCurrency(entry.unit_price)}/unité
                              </p>
                              {entry.supplier && (
                                <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                  <Truck className="h-3 w-3" />
                                  Fournisseur: {entry.supplier}
                                </p>
                              )}
                              {entry.notes && (
                                <p className="text-xs text-gray-500 mt-1">💭 {entry.notes}</p>
                              )}
                              {editingEntry === entry.id && (
                                <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                                  <p className="text-xs font-medium text-orange-800 mb-2">Modification de l'achat :</p>
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <input
                                      type="text"
                                      value={editData.designation || ''}
                                      onChange={(e) => setEditData(prev => ({...prev, designation: e.target.value}))}
                                      className="px-2 py-1 border rounded"
                                      placeholder="Produit"
                                    />
                                    <input
                                      type="number"
                                      step="0.1"
                                      value={editData.quantity || ''}
                                      onChange={(e) => setEditData(prev => ({...prev, quantity: e.target.value}))}
                                      className="px-2 py-1 border rounded"
                                      placeholder="Quantité"
                                    />
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={editData.unit_price || ''}
                                      onChange={(e) => setEditData(prev => ({...prev, unit_price: e.target.value}))}
                                      className="px-2 py-1 border rounded"
                                      placeholder="Prix unitaire"
                                    />
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={editData.total_price || ''}
                                      onChange={(e) => setEditData(prev => ({...prev, total_price: e.target.value}))}
                                      className="px-2 py-1 border rounded"
                                      placeholder="Prix total"
                                    />
                                  </div>
                                  <input
                                    type="text"
                                    value={editData.supplier || ''}
                                    onChange={(e) => setEditData(prev => ({...prev, supplier: e.target.value}))}
                                    className="w-full px-2 py-1 border rounded mt-2"
                                    placeholder="Fournisseur"
                                  />
                                </div>
                              )}
                              {entry.receipts && entry.receipts.length > 0 && (
                                <div className="mt-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Camera className="h-3 w-3 text-indigo-600" />
                                    <span className="text-xs font-medium text-indigo-800">
                                      {entry.receipts.length} reçu(s) d'achat
                                    </span>
                                  </div>
                                  <div className="flex gap-2 flex-wrap">
                                    {entry.receipts.map((receipt, receiptIndex) => (
                                      <div key={receiptIndex} className="relative group">
                                        <img
                                          src={receipt}
                                          alt={`Reçu ${receiptIndex + 1}`}
                                          className="w-16 h-16 object-cover rounded-lg border border-gray-300 hover:border-indigo-400 transition-colors cursor-pointer"
                                        />
                                        
                                        {/* Overlay with actions */}
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all rounded-lg flex items-center justify-center">
                                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                            {/* View button */}
                                            <button
                                              onClick={() => {
                                                const modal = window.open('', '_blank', 'width=800,height=600');
                                                modal.document.write(`
                                                  <html>
                                                    <head><title>Reçu d'achat - ${entry.designation}</title></head>
                                                    <body style="margin:0;background:#f3f4f6;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:Inter,sans-serif;">
                                                      <div style="background:white;border-radius:8px;padding:20px;box-shadow:0 4px 6px rgba(0,0,0,0.1);margin:20px;">
                                                        <h3 style="margin:0 0 10px 0;color:#374151;">Reçu d'achat</h3>
                                                        <p style="margin:0 0 15px 0;color:#6b7280;font-size:14px;">${entry.designation} - ${formatCurrency(entry.total_price)}</p>
                                                        <img src="${receipt}" style="max-width:100%;max-height:70vh;object-fit:contain;border-radius:8px;" alt="Reçu d'achat" />
                                                        <div style="margin-top:15px;text-align:center;">
                                                          <button onclick="window.close()" style="background:#6366f1;color:white;border:none;padding:8px 16px;border-radius:6px;cursor:pointer;">Fermer</button>
                                                        </div>
                                                      </div>
                                                    </body>
                                                  </html>
                                                `);
                                              }}
                                              className="p-1 bg-white rounded-full shadow-sm hover:bg-gray-100"
                                              title="Voir le reçu"
                                            >
                                              <Eye className="h-3 w-3 text-gray-700" />
                                            </button>
                                            
                                            {/* Download button */}
                                            <button
                                              onClick={() => {
                                                try {
                                                  const link = document.createElement('a');
                                                  link.href = receipt;
                                                  link.download = `recu_${entry.designation}_${entry.date}.jpg`;
                                                  document.body.appendChild(link);
                                                  link.click();
                                                  document.body.removeChild(link);
                                                  toast.success('Reçu téléchargé !');
                                                } catch (error) {
                                                  toast.error('Erreur lors du téléchargement');
                                                }
                                              }}
                                              className="p-1 bg-white rounded-full shadow-sm hover:bg-gray-100"
                                              title="Télécharger le reçu"
                                            >
                                              <Download className="h-3 w-3 text-green-600" />
                                            </button>
                                          </div>
                                        </div>
                                        
                                        {/* Receipt number indicator */}
                                        <div className="absolute -top-1 -right-1 bg-indigo-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                          {receiptIndex + 1}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge variant="secondary" className="bg-indigo-100 text-indigo-800 mb-1">
                              {formatCurrency(entry.total_price)}
                            </Badge>
                            {entry.receipts && entry.receipts.length > 0 && (
                              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <Camera className="h-3 w-3" />
                                {entry.receipts.length} reçu(s)
                              </p>
                            )}
                            
                            {/* Boutons de gestion */}
                            {canDelete() && (
                              <div className="flex gap-1 mt-2">
                                {editingEntry === entry.id ? (
                                  <>
                                    <button
                                      onClick={() => saveEditStock(entry.id)}
                                      className="p-1 bg-green-100 hover:bg-green-200 rounded transition-colors"
                                      title="Sauvegarder"
                                    >
                                      <Save className="h-3 w-3 text-green-600" />
                                    </button>
                                    <button
                                      onClick={cancelEditStock}
                                      className="p-1 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                                      title="Annuler"
                                    >
                                      <X className="h-3 w-3 text-gray-600" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => startEditStock(entry)}
                                      className="p-1 bg-orange-100 hover:bg-orange-200 rounded transition-colors"
                                      title="Modifier cet achat"
                                      data-testid={`edit-stock-${entry.id}`}
                                    >
                                      <Edit className="h-3 w-3 text-orange-600" />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(entry.id)}
                                      className="p-1 hover:bg-red-100 rounded transition-colors"
                                      title="Supprimer cet achat"
                                      data-testid={`delete-stock-${entry.id}`}
                                    >
                                      <Trash2 className="h-3 w-3 text-gray-400 hover:text-red-600" />
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }).filter(Boolean)}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucun achat de stock enregistré aujourd'hui</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Daily Stock Summary */}
        {Array.isArray(recentEntries) && recentEntries.length > 0 && (
          <Card className="max-width-cards mt-8 bg-white/70 glass border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-purple-600" />
                Résumé du {formatDate(selectedDate)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">
                    {recentEntries.length}
                  </p>
                  <p className="text-sm text-purple-800">Achats effectués</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">
                    {recentEntries.reduce((sum, entry) => sum + (entry.quantity || 0), 0)}
                  </p>
                  <p className="text-sm text-blue-800">Articles achetés</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(recentEntries.reduce((sum, entry) => sum + (entry.total_price || 0), 0))}
                  </p>
                  <p className="text-sm text-green-800">Total dépensé</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stock Détaillé par Lots - Traçabilité Financière */}
        {detailedStock.length > 0 && (
          <Card className="max-width-cards mt-8 bg-white/70 glass border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-purple-600" />
                Stock Disponible par Lots - Traçabilité Financière
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {detailedStock.map((stockItem, index) => (
                  <div key={index} className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-lg text-gray-900 mb-2">{stockItem.designation}</h4>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">📦 <strong>Quantité totale :</strong></p>
                            <p className="font-bold text-purple-600">{stockItem.total_quantity.toLocaleString('fr-FR')} unités</p>
                          </div>
                          <div>
                            <p className="text-gray-600">💰 <strong>Valeur totale :</strong></p>
                            <p className="font-bold text-green-600">{stockItem.total_value.toLocaleString('fr-FR')} F CFA</p>
                          </div>
                          <div>
                            <p className="text-gray-600">📊 <strong>Prix moyen :</strong></p>
                            <p className="font-bold text-blue-600">{stockItem.average_price.toLocaleString('fr-FR')} F CFA/unité</p>
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        onClick={() => setShowBatchDetails(prev => ({
                          ...prev,
                          [stockItem.designation]: !prev[stockItem.designation]
                        }))}
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Eye className="h-3 w-3" />
                        {showBatchDetails[stockItem.designation] ? 'Masquer' : 'Détails'} Lots
                      </Button>
                    </div>

                    {/* Détail des lots */}
                    {showBatchDetails[stockItem.designation] && (
                      <div className="space-y-3 mt-4 p-3 bg-white rounded-lg border">
                        <h5 className="font-medium text-gray-800 mb-3">📋 Détail des Lots (Ordre FIFO) :</h5>
                        
                        {stockItem.batches.map((batch, batchIndex) => (
                          <div key={batchIndex} className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded border border-blue-200">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div>
                                <p className="text-gray-600 font-medium">📅 Date d'achat :</p>
                                <p className="font-semibold">
                                  {new Date(batch.purchase_date).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-medium">📦 Quantité restante :</p>
                                <p className="font-bold text-blue-600">
                                  {batch.remaining_quantity.toLocaleString('fr-FR')} unités
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-medium">💰 Prix d'achat :</p>
                                <p className="font-bold text-green-600">
                                  {batch.unit_price.toLocaleString('fr-FR')} F CFA/unité
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600 font-medium">💳 Valeur du lot :</p>
                                <p className="font-bold text-purple-600">
                                  {batch.batch_value.toLocaleString('fr-FR')} F CFA
                                </p>
                              </div>
                            </div>
                            
                            <div className="mt-2 pt-2 border-t border-blue-200 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono bg-blue-100 px-2 py-1 rounded">
                                  {batch.batch_number}
                                </span>
                                {batch.supplier && (
                                  <span className="text-xs text-gray-600">
                                    Fournisseur: {batch.supplier}
                                  </span>
                                )}
                              </div>
                              
                              <Badge variant={batchIndex === 0 ? "default" : "secondary"} className="text-xs">
                                {batchIndex === 0 ? '🥇 Premier à utiliser (FIFO)' : `🔄 Lot ${batchIndex + 1}`}
                              </Badge>
                            </div>
                          </div>
                        ))}
                        
                        {/* Exemple d'utilisation FIFO */}
                        <div className="mt-4 p-3 bg-yellow-50 rounded border border-yellow-200">
                          <p className="text-xs font-medium text-yellow-800 mb-1">💡 Méthode FIFO :</p>
                          <p className="text-xs text-yellow-700">
                            Lors de l'approvisionnement, les lots les plus anciens sont utilisés en premier. 
                            Le coût réel sera calculé automatiquement selon les lots consommés.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default StockEntry;